/**
 * Frame dimension match: whether a frame's printed sizes fall inside the ranges the optician selected.
 *
 * Partial by nature. Lens width, bridge and temple are useful, but they cannot establish that a frame fits — there is no total width, lens height, wrap or bridge geometry here, and nothing about the wearer — so a match says "matches the selected frame size" and never more. Ranges are whole millimetres with both ends included. The shortcut reads a size printed on a temple and widens each number by the rule set's default selectable tolerances; an explicit range for a dimension always replaces the shortcut's.
 *
 * Each selected range is worth an equal share of the dimension points. A range can only be checked against a frame that records that dimension: when the catalog does not, the range is not evaluable for that frame and adds 0 points out of 0 — in rank mode it is never counted as a miss. Only "only this size", a hard filter the optician chose, excludes a frame that does not record a selected dimension.
 */
import {
  DIMENSIONS,
  frameDimensionValue,
  type Dimension,
  type DimensionRanges,
  type DimensionValues,
  type FrameDimensions,
  type MmRange,
} from './dimension';
import { FrameInputError } from './errors';
import { NoteCode, ReasonCode, type DimensionComparison, type Note, type Reason } from './reasons';
import type { FrameRuleSet } from './rule-set';

export const DimensionMode = {
  /** Matching ranges add points and reasons; nothing is excluded. */
  RANK: 'RANK',
  /** A hard filter: a frame outside any selected range, or not recording a selected dimension, is excluded. */
  ONLY: 'ONLY',
} as const;
export type DimensionMode = (typeof DimensionMode)[keyof typeof DimensionMode];

export interface DimensionCriteria {
  readonly mode: DimensionMode;
  readonly ranges: DimensionRanges;
}

export const ReferenceSizeProblem = {
  EMPTY: 'EMPTY',
  /** Anything outside the size grammar: letters, decimals, stray or doubled separators, a number of four digits or more. */
  NOT_A_SIZE: 'NOT_A_SIZE',
  /** Latin and Arabic-Indic digits, or the two Arabic-Indic sets, in one size. */
  MIXED_DIGITS: 'MIXED_DIGITS',
  TOO_MANY_PARTS: 'TOO_MANY_PARTS',
  OUT_OF_BOUNDS: 'OUT_OF_BOUNDS',
} as const;
export type ReferenceSizeProblem = (typeof ReferenceSizeProblem)[keyof typeof ReferenceSizeProblem];

export type ReferenceSizeResult =
  | { readonly ok: true; readonly values: DimensionValues }
  | { readonly ok: false; readonly problem: ReferenceSizeProblem };

// Longer than any printed size with generous spacing; refusing it early keeps parsing trivially bounded.
const MAX_SIZE_TEXT_LENGTH = 40;
const LATIN_DIGITS = /[0-9]/u;
const ARABIC_INDIC_DIGITS = /[\u0660-\u0669]/u;
const EXTENDED_ARABIC_INDIC_DIGITS = /[\u06F0-\u06F9]/u;
// One to three digits, separated by one hyphen, slash or box sign with optional spaces around it, or by spaces alone.
const SIZE_GRAMMAR = /^[0-9]{1,3}(?:(?:\s*[-/\u25A1]\s*|\s+)[0-9]{1,3})*$/u;

/** Replaces Arabic-Indic and Extended Arabic-Indic digits with Latin ones. Nothing else is touched. */
function toLatinSizeDigits(text: string): string {
  return text.replace(/[\u0660-\u0669\u06F0-\u06F9]/gu, (digit) => {
    const code = digit.charCodeAt(0);
    return String(code - (code >= 0x06f0 ? 0x06f0 : 0x0660));
  });
}

/** Reads "52-18-140", "52□18 140", "52/18/140" or "52 18" — lens, then bridge, then temple, each part optional after the first — in Latin or Arabic-Indic digits. Anything else is refused whole, never partly read. */
export function parseReferenceSize(text: string, rules: FrameRuleSet): ReferenceSizeResult {
  const trimmed = text.trim();
  if (trimmed.length === 0) return { ok: false, problem: ReferenceSizeProblem.EMPTY };
  if (trimmed.length > MAX_SIZE_TEXT_LENGTH) {
    return { ok: false, problem: ReferenceSizeProblem.NOT_A_SIZE };
  }

  const scripts = [LATIN_DIGITS, ARABIC_INDIC_DIGITS, EXTENDED_ARABIC_INDIC_DIGITS].filter(
    (digits) => digits.test(trimmed),
  );
  if (scripts.length > 1) return { ok: false, problem: ReferenceSizeProblem.MIXED_DIGITS };

  const latin = toLatinSizeDigits(trimmed);
  if (!SIZE_GRAMMAR.test(latin)) return { ok: false, problem: ReferenceSizeProblem.NOT_A_SIZE };

  const parts = latin.match(/[0-9]+/g) ?? [];
  if (parts.length > DIMENSIONS.length) {
    return { ok: false, problem: ReferenceSizeProblem.TOO_MANY_PARTS };
  }

  const values: Partial<Record<Dimension, number>> = {};
  for (const [index, part] of parts.entries()) {
    const dimension = DIMENSIONS[index];
    if (dimension === undefined) return { ok: false, problem: ReferenceSizeProblem.TOO_MANY_PARTS };
    const value = Number.parseInt(part, 10);
    const bounds = rules.dimensions.boundsMm[dimension];
    if (value < bounds.minMm || value > bounds.maxMm) {
      return { ok: false, problem: ReferenceSizeProblem.OUT_OF_BOUNDS };
    }
    values[dimension] = value;
  }
  return { ok: true, values };
}

/** Widens each reference value by its tolerance, clamped to the catalog's bounds. */
export function rangesFromReference(
  values: DimensionValues,
  rules: FrameRuleSet,
  tolerancesMm: Readonly<Record<Dimension, number>> = rules.dimensions.defaultTolerancesMm,
): DimensionRanges {
  const ranges: Partial<Record<Dimension, MmRange>> = {};
  for (const dimension of DIMENSIONS) {
    const value = values[dimension];
    if (value === undefined) continue;
    const bounds = rules.dimensions.boundsMm[dimension];
    if (!Number.isInteger(value) || value < bounds.minMm || value > bounds.maxMm) {
      throw new FrameInputError(
        `${dimension} reference ${value} mm is outside ${bounds.minMm}–${bounds.maxMm} mm`,
      );
    }
    const tolerance = tolerancesMm[dimension];
    if (!Number.isInteger(tolerance) || tolerance < 0) {
      throw new FrameInputError(`${dimension} tolerance must be whole millimetres ≥ 0`);
    }
    ranges[dimension] = {
      minMm: Math.max(bounds.minMm, value - tolerance),
      maxMm: Math.min(bounds.maxMm, value + tolerance),
    };
  }
  return ranges;
}

/** Refuses a range that is not whole millimetres, is back to front, or leaves the catalog's bounds. */
export function assertValidRange(dimension: Dimension, range: MmRange, rules: FrameRuleSet): void {
  const bounds = rules.dimensions.boundsMm[dimension];
  if (!Number.isInteger(range.minMm) || !Number.isInteger(range.maxMm)) {
    throw new FrameInputError(`${dimension} range must be whole millimetres`);
  }
  if (range.minMm > range.maxMm) {
    throw new FrameInputError(`${dimension} range has its minimum above its maximum`);
  }
  if (range.minMm < bounds.minMm || range.maxMm > bounds.maxMm) {
    throw new FrameInputError(
      `${dimension} range must stay within ${bounds.minMm}–${bounds.maxMm} mm`,
    );
  }
}

/** The ranges that apply: the shortcut's, with any explicit range replacing the shortcut's for its dimension. */
export function resolveDimensionRanges(
  sources: {
    readonly reference?: DimensionValues | null;
    readonly explicit?: DimensionRanges | null;
  },
  rules: FrameRuleSet,
): DimensionRanges {
  const fromReference = sources.reference ? rangesFromReference(sources.reference, rules) : {};
  const resolved: Partial<Record<Dimension, MmRange>> = {};
  for (const dimension of DIMENSIONS) {
    const explicit = sources.explicit?.[dimension];
    if (explicit) {
      assertValidRange(dimension, explicit, rules);
      resolved[dimension] = { minMm: explicit.minMm, maxMm: explicit.maxMm };
    } else if (fromReference[dimension]) {
      resolved[dimension] = fromReference[dimension];
    }
  }
  return resolved;
}

export interface DimensionContribution {
  /** At least one range is selected. */
  readonly requested: boolean;
  /** True only in rank mode: a filter adds no points, because every remaining frame satisfies it. */
  readonly scoring: boolean;
  /** Selected dimensions this frame records, in printed order. */
  readonly evaluated: readonly Dimension[];
  readonly points: number;
  /** In rank mode, an equal share of the dimension points for each selected range this frame records. */
  readonly maxPoints: number;
  /** False when "only this size" excludes the frame. */
  readonly passes: boolean;
  readonly reasons: readonly Reason[];
  readonly notes: readonly Note[];
}

const NOT_REQUESTED: DimensionContribution = Object.freeze({
  requested: false,
  scoring: false,
  evaluated: [],
  points: 0,
  maxPoints: 0,
  passes: true,
  reasons: [],
  notes: [],
});

/** Points each selected range is worth: the dimension points split evenly across the selected ranges. */
export const pointsPerRange = (selectedCount: number, rules: FrameRuleSet): number =>
  selectedCount === 0 ? 0 : rules.dimensions.maxPoints / selectedCount;

export const selectedDimensions = (criteria: DimensionCriteria | null): readonly Dimension[] =>
  criteria ? DIMENSIONS.filter((dimension) => criteria.ranges[dimension] !== undefined) : [];

export function dimensionContribution(
  criteria: DimensionCriteria | null,
  frame: FrameDimensions,
  rules: FrameRuleSet,
): DimensionContribution {
  const selected = selectedDimensions(criteria);
  if (!criteria || selected.length === 0) return NOT_REQUESTED;

  const evaluated: Dimension[] = [];
  const matched: DimensionComparison[] = [];
  const notes: Note[] = [];
  for (const dimension of selected) {
    const range = criteria.ranges[dimension];
    if (!range) continue;
    const valueMm = frameDimensionValue(frame, dimension);
    if (valueMm === null) {
      notes.push({ code: NoteCode.DIMENSION_NOT_RECORDED, params: { dimension } });
      continue;
    }
    if (!Number.isInteger(valueMm) || valueMm <= 0) {
      throw new FrameInputError(
        `A frame's ${dimension} must be whole millimetres > 0 or not recorded`,
      );
    }
    evaluated.push(dimension);
    const comparison: DimensionComparison = {
      dimension,
      valueMm,
      minMm: range.minMm,
      maxMm: range.maxMm,
    };
    if (valueMm >= range.minMm && valueMm <= range.maxMm) matched.push(comparison);
    else notes.push({ code: NoteCode.DIMENSION_OUTSIDE_RANGE, params: comparison });
  }

  const scoring = criteria.mode === DimensionMode.RANK;
  const share = pointsPerRange(selected.length, rules);
  const reasons: Reason[] =
    matched.length > 0
      ? [{ code: ReasonCode.DIMENSION_MATCH, params: { dimensions: matched } }]
      : [];

  return {
    requested: true,
    scoring,
    evaluated,
    points: scoring ? matched.length * share : 0,
    maxPoints: scoring ? evaluated.length * share : 0,
    passes: scoring || matched.length === selected.length,
    reasons,
    notes,
  };
}
